import { KeyboardEvent, ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { RowMenu } from "@/components/ui/RowMenu";
import { type DropdownMenuEntry } from "@/components/ui/DropdownMenu";
import { PlusIcon } from "@/components/icons";
import { cn } from "@/utils/cn";

export interface VersionEntry {
  id: string;
  title: string;
  /** One short line under the title, e.g. "12 posts" or "from 1 Jul 2026". */
  meta?: ReactNode;
  /** Its state, e.g. Active, Scheduled, Past. Only one version is active at a time. */
  status?: { label: string; tone: BadgeTone };
  /** Actions on this version other than opening it (DD-045), e.g. set active, duplicate, delete. */
  actions?: DropdownMenuEntry[];
}

interface VersionsLayoutProps {
  versions: VersionEntry[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** e.g. "New listino". Omit to hide the create button. */
  createLabel?: string;
  onCreate?: () => void;
  createDisabled?: boolean;
  empty?: ReactNode;
  /** The selected version's details: a grid, a price list, anything. */
  children: ReactNode;
  className?: string;
}

/**
 * Versioned things on one page (DD-048): the versions on the left, one of them active, and the
 * selected version's details on the right. Lifted from the grid page's left panel. Selecting a
 * version is the card's click; its other actions sit in the card's menu, never as hover buttons.
 */
export function VersionsLayout({ versions, selectedId, onSelect, createLabel, onCreate, createDisabled, empty = "No versions yet.", children, className }: VersionsLayoutProps) {
  const onKey = (id: string) => (e: KeyboardEvent) => {
    // Keys from the nested menu trigger or its portal'd items are the menu's, not the card's.
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect(id);
    }
  };
  return (
    <div className={cn("flex-1 min-h-0 flex flex-col md:flex-row gap-4 px-4 lg:px-6 pb-4 lg:pb-6", className)}>
      <nav aria-label="Versions" className="md:w-48 xl:w-64 shrink-0 flex flex-col gap-3 md:min-h-0">
        {onCreate && createLabel && (
          <Button onClick={onCreate} disabled={createDisabled} className="w-full">
            <PlusIcon size={16} className="mr-1.5" />
            {createLabel}
          </Button>
        )}
        <div className="flex-1 min-h-0 flex gap-2 overflow-x-auto md:block md:overflow-x-visible md:overflow-y-auto md:space-y-2 p-0.5">
          {versions.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">{empty}</p>
          ) : (
            versions.map((v) => (
              <div
                key={v.id}
                role="button"
                tabIndex={0}
                aria-current={v.id === selectedId ? "true" : undefined}
                onClick={() => onSelect(v.id)}
                onKeyDown={onKey(v.id)}
                className={cn(
                  // Borderless card (DD-011): selection reads as a ring.
                  "rounded-lg p-3 bg-white cursor-pointer w-44 shrink-0 md:w-auto md:shrink transition-all focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-300",
                  v.id === selectedId ? "ring-2 ring-blue-500" : "hover:shadow-md",
                )}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">{v.title}</div>
                    {v.meta && <div className="text-xs text-gray-500 mt-0.5">{v.meta}</div>}
                  </div>
                  {v.actions && <RowMenu items={v.actions} label={`${v.title} actions`} />}
                </div>
                {v.status && <Badge tone={v.status.tone} className="mt-2">{v.status.label}</Badge>}
              </div>
            ))
          )}
        </div>
      </nav>
      <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-3">{children}</div>
    </div>
  );
}
